import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Search, Plus, Edit2, Trash2, Wrench, Clock, DollarSign, Tag
} from "lucide-react";

const CATEGORIES = ["General", "Battery", "Motor", "Charger", "Diagnostic", "Body Work", "Electrical", "Software", "Accessories"];

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchServices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/services`);
      setServices(r.data);
    } catch { toast.error("Failed to fetch services"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(services); return; }
    const q = search.toLowerCase();
    setFiltered(services.filter(s =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.category || "").toLowerCase().includes(q) ||
      (s.description || "").toLowerCase().includes(q)
    ));
  }, [services, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success("Service deleted");
      fetchServices();
    } catch { toast.error("Failed to delete"); }
  };

  const handleEdit = (svc) => {
    setEditing(svc);
    setModalOpen(true);
  };

  const activeCount = services.filter(s => s.is_active).length;
  const categories = [...new Set(services.map(s => s.category || "General"))];

  return (
    <div className="p-5 space-y-5 overflow-y-auto h-[calc(100vh-60px)]" data-testid="services-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-[#1E293B]">Services</h1>
          <p className="text-[13px] text-[#94A3B8]">Manage service types and pricing for your workshop</p>
        </div>
        <Button data-testid="add-service-btn" onClick={() => { setEditing(null); setModalOpen(true); }}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] h-9 gap-1.5 font-semibold rounded-lg">
          <Plus className="w-3.5 h-3.5" /> Add Service
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Wrench} iconBg="bg-[#EFF6FF]" iconColor="text-[#3B82F6]" label="Total Services" value={services.length} />
        <StatCard icon={Tag} iconBg="bg-[#F0FDF4]" iconColor="text-[#16A34A]" label="Active" value={activeCount} />
        <StatCard icon={Tag} iconBg="bg-[#FEF2F2]" iconColor="text-[#DC2626]" label="Inactive" value={services.length - activeCount} />
        <StatCard icon={Tag} iconBg="bg-[#FEF3C7]" iconColor="text-[#D97706]" label="Categories" value={categories.length} />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input data-testid="service-search" placeholder="Search services..." value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 text-[13px] bg-[#F8FAFC] border-[#E2E8F0]" />
      </div>

      {/* Service cards grid */}
      {loading ? <p className="text-center py-12 text-[13px] text-[#94A3B8]">Loading...</p> :
       filtered.length === 0 ? (
         <div className="text-center py-16">
           <Wrench className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
           <p className="text-[14px] text-[#94A3B8]">{search ? "No services match your search" : "No services yet"}</p>
           <p className="text-[12px] text-[#CBD5E1] mt-1">Add your first service to get started</p>
         </div>
       ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {filtered.map(svc => (
             <div key={svc.id} data-testid={`service-card-${svc.id}`}
               className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm hover:shadow-md transition-shadow">
               <div className="p-5">
                 <div className="flex items-start justify-between mb-3">
                   <div className="flex items-center gap-2.5">
                     <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${svc.is_active ? "bg-[#EFF6FF]" : "bg-[#F1F5F9]"}`}>
                       <Wrench className={`w-5 h-5 ${svc.is_active ? "text-[#3B82F6]" : "text-[#94A3B8]"}`} />
                     </div>
                     <div>
                       <h3 className="text-[14px] font-bold text-[#1E293B]">{svc.name}</h3>
                       <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F8FAFC] text-[#64748B] border border-[#E2E8F0]">{svc.category || "General"}</span>
                     </div>
                   </div>
                   <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${svc.is_active ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>
                     {svc.is_active ? "Active" : "Inactive"}
                   </span>
                 </div>

                 {svc.description && <p className="text-[12px] text-[#64748B] mb-3 line-clamp-2">{svc.description}</p>}

                   <div className="flex items-center gap-4 text-[12px]">
                   <div className="flex items-center gap-1 text-[#475569]">
                     <DollarSign className="w-3.5 h-3.5 text-[#94A3B8]" />
                     <span className="font-semibold">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(svc.base_price || 0)}</span>
                   </div>
                   {svc.estimated_duration && (
                     <div className="flex items-center gap-1 text-[#475569]">
                       <Clock className="w-3.5 h-3.5 text-[#94A3B8]" />
                       {svc.estimated_duration}
                     </div>
                   )}
                 </div>
               </div>

               <div className="border-t border-[#F1F5F9] px-5 py-2.5 flex items-center justify-end gap-2">
                 <button data-testid={`edit-service-${svc.id}`} onClick={() => handleEdit(svc)}
                   className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-[#64748B] transition-colors">
                   <Edit2 className="w-3.5 h-3.5" />
                 </button>
                 <button data-testid={`delete-service-${svc.id}`} onClick={() => handleDelete(svc.id)}
                   className="p-1.5 rounded-lg hover:bg-[#FEF2F2] text-[#DC2626] transition-colors">
                   <Trash2 className="w-3.5 h-3.5" />
                 </button>
               </div>
             </div>
           ))}
         </div>
       )}

      {/* Create/Edit Modal */}
      <ServiceModal open={modalOpen} editing={editing}
        onClose={(refresh) => { setModalOpen(false); setEditing(null); if (refresh) fetchServices(); }} />
    </div>
  );
};

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9]">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center mb-2`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
    <p className="text-[24px] font-bold text-[#1E293B]">{value}</p>
  </div>
);

const ServiceModal = ({ open, editing, onClose }) => {
  const [data, setData] = useState({
    name: "", description: "", category: "General",
    base_price: "", estimated_duration: "", is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      if (editing) {
        setData({
          name: editing.name || "", description: editing.description || "",
          category: editing.category || "General", base_price: editing.base_price || "",
          estimated_duration: editing.estimated_duration || "", is_active: editing.is_active !== false,
        });
      } else {
        setData({ name: "", description: "", category: "General", base_price: "", estimated_duration: "", is_active: true });
      }
    }
  }, [open, editing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.name.trim()) { toast.error("Service name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...data, base_price: data.base_price ? Number(data.base_price) : 0 };
      if (editing) {
        await axios.put(`${API}/services/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success("Service added");
      }
      onClose(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="service-modal">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{editing ? "Edit Service" : "Add New Service"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px]">Service Name *</Label>
            <Input data-testid="service-name-input" value={data.name} onChange={e => set("name", e.target.value)}
              className="h-9 mt-1 text-[13px]" placeholder="e.g. Battery Replacement" required />
          </div>
          <div>
            <Label className="text-[11px]">Description</Label>
            <Textarea data-testid="service-description" value={data.description} onChange={e => set("description", e.target.value)}
              className="mt-1 text-[13px]" rows={2} placeholder="Brief description of the service" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Category</Label>
              <select data-testid="service-category" value={data.category} onChange={e => set("category", e.target.value)}
                className="w-full h-9 mt-1 text-[13px] border border-[#E2E8F0] rounded-md px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px]">Base Price (₹)</Label>
              <Input data-testid="service-price" type="number" value={data.base_price}
                onChange={e => set("base_price", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Estimated Duration</Label>
              <Input data-testid="service-duration" value={data.estimated_duration}
                onChange={e => set("estimated_duration", e.target.value)} className="h-9 mt-1 text-[13px]" placeholder="e.g. 2 hours" />
            </div>
            <div className="flex items-end pb-1">
              <div className="flex items-center gap-2.5">
                <Switch data-testid="service-active-toggle" checked={data.is_active} onCheckedChange={v => set("is_active", v)} />
                <Label className="text-[12px] text-[#475569]">{data.is_active ? "Active" : "Inactive"}</Label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onClose(false)} className="text-[12px]">Cancel</Button>
            <Button data-testid="submit-service-btn" type="submit" disabled={saving}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">
              {saving ? "Saving..." : editing ? "Update" : "Add Service"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ServicesPage;
